import * as React from 'react'
import classNames from 'classnames'
import { SVG_Success } from '../../../app-core/lib/svg/SVG_Success'
import { SVG_Warning } from '../../../app-core/lib/svg/SVG_Warning'
import { Spinner } from '../../../app-core/lib/ui/Spinner'
import type { Base_IContext } from '../Base/Base.IContext'
import { Base_PureComponent } from '../Base/Base.ui.Component'
import { Translations } from '../../../shared/i18n/Translations'

type TProps = {
    buttonText: string
    style: 'primary' | 'neutral' | 'warning' | 'green' | 'disabled'
    onClick?: (e: React.MouseEvent<HTMLDivElement>) => void
} & Partial<TDefaultProps>

type TDefaultProps = {
    status: 'ready' | 'spin' | 'error' | 'success'
    disabled: boolean // controls only the behaviour of onClick, control the disabled style using style
}

export class UI_RoundedButton extends Base_PureComponent<TProps, {}> {
    public static defaultProps: TDefaultProps = {
        status: 'ready',
        disabled: false,
    }

    private onClick = (e: React.MouseEvent<HTMLDivElement>) => {
        const { onClick, disabled } = this.props
        e.preventDefault()
        if (disabled) return
        if (onClick) onClick(e)
    }

    public tryRender() {
        const { buttonText, style, status } = this.props

        return (
            <div
                className={classNames('UI_RoundedButton_root', 'clickable', `UI_RoundedButton_${style}`)}
                onClick={this.onClick}
            >
                {status === 'spin' && <Spinner />}
                {status === 'success' && <SVG_Success className="UI_RoundedButton_success" />}
                {status === 'error' && <SVG_Warning className="UI_RoundedButton_error" />}
                <span> {buttonText} </span>
            </div>
        )
    }
}

type TTryCatchProps = {
    buttonText: string
    loadingText?: string
    successText?: string
    errorText?: string
    style: 'primary' | 'neutral' | 'warning' | 'green'
    onClick: (e: React.MouseEvent<HTMLDivElement>) => Promise<void>
} & Partial<TTryCatchDefaultProps>

type TTryCatchDefaultProps = {
    showSuccess: boolean
    disabled: boolean
}

type TState = {
    status: 'ready' | 'spin' | 'error' | 'success'
}

export class UI_RoundedButtonTryCatch extends Base_PureComponent<TTryCatchProps, TState> {
    public static defaultProps: TTryCatchDefaultProps = {
        showSuccess: true,
        disabled: false,
    }

    constructor(props: TTryCatchProps, context: Base_IContext) {
        super(props, context)

        this.state = {
            status: 'ready',
        }
    }

    private tryOnClick = async (e: React.MouseEvent<HTMLDivElement>) => {
        const { onClick, showSuccess } = this.props

        e.preventDefault()

        try {
            this.setState({ status: 'spin' })
            await onClick(e)
            this.setState({ status: showSuccess ? 'success' : 'ready' })
        } catch (err) {
            this.setState({ status: 'error' })
        }
    }

    public tryRender() {
        let { buttonText, disabled } = this.props
        let style: TProps['style'] = this.props.style

        switch (this.state.status) {
            case 'spin':
                buttonText = this.props.loadingText || Translations.t('app:UI.common.workingOnIt')
                style = 'disabled'
                disabled = true
                break
            case 'success':
                buttonText = this.props.successText || Translations.t('app:UI.common.success')
                style = 'green'
                disabled = true
                break
            case 'error':
                buttonText = this.props.errorText || Translations.t('app:UI.common.somethingWentWrong')
                style = 'warning'
                break
        }

        if (this.props.disabled) {
            style = 'disabled'
        }

        return (
            <UI_RoundedButton
                buttonText={buttonText}
                style={style}
                disabled={disabled}
                onClick={this.tryOnClick}
                status={this.state.status}
            />
        )
    }
}
